<template>
  <div id="app" class="our-bg-color">
    <ToastHost />

    <nav v-if="
      !$route.fullPath.includes('print') &&
      !$route.fullPath.includes('status')
    " id="mainNavbar"
      class="tw-sticky tw-top-0 tw-z-50 tw-border-b tw-border-slate-200/70 tw-bg-white/80 tw-backdrop-blur-lg tw-shadow-sm tw-shadow-slate-900/5">
      <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-px-3 sm:tw-px-5 tw-py-2" @logIn="logIn">

        <div class="tw-flex tw-items-stretch tw-h-16">
          <a class="tw-flex tw-items-center" href="/">
            <img src="@/assets/logo.webp" alt="Contrak" class="tw-h-full tw-w-auto tw-object-contain" />
          </a>
        </div>

        <button type="button"
          class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white md:tw-hidden"
          :aria-expanded="Boolean(isNavOpen)" aria-label="Toggle navigation" @click="isNavOpen = !isNavOpen">
          <span class="tw-h-5 tw-w-5">☰</span>
        </button>

        <ul
          class="tw-absolute tw-left-0 tw-right-0 tw-top-full tw-z-50 tw-border-b tw-border-slate-200/70 tw-bg-white/95 tw-backdrop-blur md:tw-static md:tw-border-0 md:tw-bg-transparent"
          :class="isNavOpen ? 'tw-block' : 'tw-hidden md:tw-flex'">
          <div
            class="tw-flex tw-flex-col tw-gap-1 tw-px-0 tw-py-3 md:tw-flex-row md:tw-items-center md:tw-justify-end md:tw-gap-2 md:tw-px-0 md:tw-py-0">

            <template v-if="isLoggedIn || canAccessAdmin">

              <router-link to="/tracking" custom v-slot="{ href, navigate, isActive }" v-if="isLoggedIn">
                <li class="tw-relative tw-group" :class="{ 'uk-active': isActive }">
                  <a :href="href" @click="navigate" class="uk-navbar-nav_a">Tracking</a>

                  <div
                    class="tw-hidden tw-min-w-max tw-pt-1 md:tw-absolute md:tw-left-0 md:tw-top-full md:tw-block md:tw-opacity-0 md:tw-pointer-events-none md:group-hover:tw-opacity-100 md:group-hover:tw-pointer-events-auto">
                    <div
                      class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white tw-shadow-lg tw-shadow-slate-900/10 tw-p-1">
                      <ul class="tw-flex tw-flex-col">
                        <router-link to="/archived" custom v-slot="{ href, navigate }">
                          <li>
                            <a :href="href" @click="navigate" class="uk-navbar-nav_a">Archived</a>
                          </li>
                        </router-link>
                      </ul>
                    </div>
                  </div>

                  <div class="md:tw-hidden tw-pl-3">
                    <router-link to="/archived" custom v-slot="{ href, navigate }">
                      <a :href="href" @click="navigate" class="uk-navbar-nav_a tw-inline-flex tw-w-full">Archived</a>
                    </router-link>
                  </div>
                </li>
              </router-link>

              <router-link to="/orders" custom v-slot="{ href, navigate, isActive }" v-if="isLoggedIn">
                <li :class="{ 'uk-active': isActive }">
                  <a :href="href" @click="navigate" class="uk-navbar-nav_a">Orders</a>
                </li>
              </router-link>

              <router-link to="/dispatch" custom v-slot="{ href, navigate, isActive }" v-if="isLoggedIn">
                <li :class="{ 'uk-active': isActive }">
                  <a :href="href" @click="navigate" class="uk-navbar-nav_a">Dispatch</a>
                </li>
              </router-link>

              <router-link to="/customers" custom v-slot="{ href, navigate, isActive }" v-if="isLoggedIn">
                <li :class="{ 'uk-active': isActive }">
                  <a :href="href" @click="navigate" class="uk-navbar-nav_a">Contacts</a>
                </li>
              </router-link>

              <router-link to="/billing" custom v-slot="{ href, navigate, isActive }" v-if="isLoggedIn">
                <li :class="{ 'uk-active': isActive }">
                  <a :href="href" @click="navigate" class="uk-navbar-nav_a">Billing</a>
                </li>
              </router-link>

              <router-link to="/reports" custom v-slot="{ href, navigate, isActive }" v-if="isLoggedIn">
                <li :class="{ 'uk-active': isActive }">
                  <a :href="href" @click="navigate" class="uk-navbar-nav_a">Reports</a>
                </li>
              </router-link>

              <router-link to="/profile" custom v-slot="{ href, navigate, isActive }" v-if="isLoggedIn">
                <li class="tw-relative tw-group">
                  <div class="tw-flex tw-items-center tw-gap-3">
                    <div
                      class="tw-hidden sm:tw-flex tw-items-stretch tw-h-11 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 hover:tw-bg-white tw-cursor-pointer tw-overflow-visible">

                      <div
                        class="tw-flex tw-items-center tw-gap-3 tw-h-full tw-px-3 hover:tw-bg-slate-900/5 tw-rounded-l-xl tw-transition-colors"
                        @click="navigate">
                        <font-awesome-icon icon="user"
                          class="tw-h-4 tw-w-4 tw-text-slate-600 tw-cursor-pointer tw-transition-colors hover:tw-text-slate-900" />
                        <div class="tw-flex tw-flex-col tw-leading-tight tw-text-left tw-cursor-pointer">
                          <div class="tw-text-xs tw-font-semibold tw-text-slate-900">{{ username() || 'User' }}</div>
                          <div v-if="userEmail()"
                            class="tw-text-[11px] tw-text-slate-600 tw-cursor-pointer tw-transition-colors hover:tw-text-slate-900">
                            {{ userEmail() }}</div>
                        </div>
                      </div>

                      <div class="tw-my-2.5 tw-w-px tw-bg-slate-200/80"></div>

                      <div class="tw-relative tw-group/gear">
                        <a :href="href" @click="navigate"
                          class="tw-flex tw-items-center tw-justify-center tw-h-full tw-px-4 tw-cursor-pointer tw-rounded-r-xl tw-transition-colors hover:tw-bg-slate-900/5"
                          aria-label="Settings" title="Settings">
                          <font-awesome-icon icon="gear"
                            class="tw-h-4 tw-w-4 tw-text-slate-700 tw-cursor-pointer tw-transition-colors hover:tw-text-slate-900" />
                        </a>

                        <div v-if="canAccessAdmin"
                          class="tw-hidden tw-min-w-max tw-pt-1 md:tw-absolute md:tw-right-0 md:tw-top-full md:tw-block md:tw-opacity-0 md:tw-pointer-events-none md:group-hover/gear:tw-opacity-100 md:group-hover/gear:tw-pointer-events-auto">
                          <div
                            class="tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white tw-shadow-lg tw-shadow-slate-900/10 tw-p-1">
                            <ul class="tw-flex tw-flex-col">
                              <router-link to="/admin" custom v-slot="{ href, navigate }">
                                <li>
                                  <a :href="href" @click="navigate" class="uk-navbar-nav_a">Admin</a>
                                </li>
                              </router-link>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="canAccessAdmin" class="md:tw-hidden tw-pl-3">
                    <router-link to="/admin" custom v-slot="{ href, navigate }">
                      <a :href="href" @click="navigate" class="uk-navbar-nav_a tw-inline-flex tw-w-full">Admin</a>
                    </router-link>
                  </div>
                </li>
              </router-link>
            </template>

            <router-link v-if="isLoggedIn" to="/" custom v-slot="{ href, navigate }">
              <li @click="logout">
                <a :href="href" @click="navigate"
                  class="tw-inline-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-p-0 tw-cursor-pointer hover:tw-bg-slate-900/5"
                  aria-label="Logout" title="Logout">
                  <font-awesome-icon icon="right-from-bracket"
                    class="tw-h-4 tw-w-4 tw-text-slate-700 tw-transition-colors hover:tw-text-slate-900 tw-cursor-pointer" />
                </a>
              </li>
            </router-link>

            <router-link v-else to="/login" custom v-slot="{ href, navigate, isActive }">
              <li :class="{ 'uk-active': isActive }">
                <a :href="href" @click="navigate" class="uk-navbar-nav_a">Login</a>
              </li>
            </router-link>

          </div>
        </ul>
      </div>
    </nav>
    <router-view @logIn="logIn"></router-view>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { ROUTE_NAMES } from './router/route_utils';
import ToastHost from '@/components/core/ToastHost.vue';
import { useAuthStore } from '@/stores/auth';
import { performLogout } from '@/apollo';

export default defineComponent({
  name: 'App',
  components: {
    ToastHost,
  },
  data() {
    return {
      isLoggedIn: false,
      isAdmin: false,
      canAccessAdmin: false,
      isNavOpen: false,
      ROUTE_NAMES,
      idleInterval: null as any,
    };
  },
  watch: {
    $route() {
      this.syncAuthState();
    }
  },
  mounted() {
    this.syncAuthState();
  },
  beforeUnmount() {
    this.stopIdleTracking();
  },
  methods: {
    async syncAuthState() {
      const authStore = useAuthStore();
      if (!authStore.hydrated) {
        authStore.hydrateFromStorage();
        await authStore.refreshRoles(true);
      }

      // Keep UI auth decisions centralized in store-derived state
      this.isLoggedIn = authStore.isAuthenticated;
      this.isAdmin = authStore.isAdmin;
      this.canAccessAdmin = authStore.canAccessAdmin;

      if (this.isLoggedIn) {
        this.startIdleTracking();
      } else {
        this.stopIdleTracking();
      }
    },
    logIn() {
      this.syncAuthState();
    },
    username() {
      return useAuthStore().username;
    },
    userEmail() {
      return useAuthStore().email;
    },
    async logout() {
      await performLogout();
      this.syncAuthState();
    },
    startIdleTracking() {
      if (this.idleInterval) return;
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      events.forEach(event => {
        document.addEventListener(event, this.resetIdleTimer);
      });
      this.idleInterval = setInterval(() => {
        this.checkIdleTime();
      }, 60000);
    },
    stopIdleTracking() {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      events.forEach(event => {
        document.removeEventListener(event, this.resetIdleTimer);
      });
      if (this.idleInterval) {
        clearInterval(this.idleInterval);
        this.idleInterval = null;
      }
    },
    resetIdleTimer() {
      useAuthStore().updateLastActivity();
    },
    checkIdleTime() {
      const TWELVE_HOURS = 12 * 60 * 60 * 1000;
      const authStore = useAuthStore();
      if (authStore.isSessionExpired(TWELVE_HOURS)) {
        this.logout();
      }
    }
  }
});
</script>

<style lang="scss">
@import 'uikit/src/scss/variables-theme.scss';
@import 'uikit/src/scss/variables.scss';
@import 'uikit/src/scss/mixins-theme.scss';

$global-link-color: #da7d02;

$global-font-family: Rubik, sans-serif;

html,
body {
  margin: 0;
  padding: 0;
  width: 100%;
}

body {
  background-color: #fff;
}

@mixin hook-card() {
  color: #000;
}

@import 'uikit/src/scss/uikit.scss';

#app {
  font-family: Rubik, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  width: 100%;
}

.header {
  position: relative;
  background-image: -webkit-gradient(linear,
      left top,
      left bottom,
      from(rgba(22, 36, 56, 0.5)),
      to(rgba(22, 36, 56, 0.5))),
    url(https://unsplash.com/photos/nfQk1YdGoNc/download?force=true&w=1920);
  background-image: linear-gradient(180deg,
      rgba(22, 36, 56, 0.5),
      rgba(22, 36, 56, 0.5)),
    url(https://unsplash.com/photos/nfQk1YdGoNc/download?force=true&w=1920);
  background-position: 0px 0px, 50% 50%;
  background-size: auto, cover;
  background-repeat: repeat, no-repeat;
}

.body {
  color: #162438;
  font-weight: 400;
}

.uk-label-black {
  background-color: #000000;
  color: #fff;
}
</style>

<style lang="scss">
/* Navbar styles */
#mainNavbar {
  background-color: transparent;
}

/* Bright, Tailwind-ish nav link styling while keeping existing markup */
#mainNavbar .logo {
  color: #0f172a;
  font-weight: 600;
  letter-spacing: -0.01em;
}

#mainNavbar .uk-navbar-nav_a {
  color: #0f172a;
  border-radius: 10px;
  padding: 8px 10px;
  line-height: 1.25;
  transition: background-color 150ms ease, color 150ms ease;
}

#mainNavbar .uk-navbar-nav_a:hover {
  color: #020617;
  background-color: rgba(255, 255, 255, 0.55);
  text-decoration: underline;
  text-underline-offset: 3px;
}

#mainNavbar li.uk-active>.uk-navbar-nav_a,
#mainNavbar li.uk-active>a.uk-navbar-nav_a {
  color: #020617;
  background-color: rgba(255, 255, 255, 0.75);
  font-weight: 700;
}

#navbarTitle {
  color: #0f172a;
  margin-top: 20px;
  margin-bottom: 20px;
}

#mainNavbar #navbarTitle {
  color: #0f172a;
}

/* --- Uniform Header with Spacing --- */
@media (min-width: 960px) and (max-width: 1350px) {

  .uk-navbar-container {
    height: auto !important;
    padding-top: 5px;
    padding-bottom: 5px;
    align-items: center;
  }

  .uk-navbar-left {
    width: auto;
    flex-shrink: 0;
    margin-right: 20px;
  }

  .uk-navbar-right {
    flex-grow: 1;
    width: auto;
    justify-content: flex-end;
  }

  .uk-navbar-nav {
    flex-wrap: wrap !important;
    justify-content: flex-end;
    column-gap: 15px;
    row-gap: 0px;
  }

  .uk-navbar-nav>li>a,
  .uk-navbar-nav>li.user-section>a {
    min-height: 30px;
    line-height: 30px;
    padding: 0;
    font-size: 0.9rem;
    white-space: nowrap;
    border: none !important;
    margin: 0 !important;
  }

  .user-section {
    margin-left: 0 !important;
    border: none !important;
    padding: 0 !important;
    display: block;
  }

  .uk-navbar-nav>li {
    padding: 0 !important;
    background: transparent !important;
  }
}

/* --- Global Navbar Styles --- */

.uk-navbar-nav_a {
  text-decoration: none;
  color: #cecece;
  line-height: 3em;
  text-transform: none !important;
  /* Важливе виправлення для шрифтів */
}

.uk-navbar-nav_a:hover {
  text-decoration: none;
  color: #666;
}

.uk-navbar-nav>li.uk-active>a {
  color: white;
  font-weight: bold;
}

.uk-navbar-nav>li>a {
  color: #cecece;
  text-transform: none !important;
}

/* For mobile screens ( < 959px ) */
@media (max-width: 959px) {
  #navbarTitle {
    font-size: 1.3rem;
  }

  .uk-navbar .toggle-target.collapsed {
    display: none;
  }

  .uk-navbar .toggle-target .uk-navbar-nav {
    display: block;
    top: 58px;
    position: absolute;
    left: 0;
    right: 0;
    width: 100%;
    background: #f8f8f8;
  }

  .uk-navbar .toggle-target .uk-navbar-nav li a {
    display: block;
    min-height: 0;
    line-height: 2em;
    padding: 0 15px !important;
  }

  .uk-navbar .toggle-target .uk-navbar-dropdown {
    width: 90%;
    min-width: 200px;
  }

  .uk-navbar .toggle-target .uk-navbar-dropdown[class*='uk-navbar-dropdown-bottom'] {
    margin-top: 0;
  }

  .uk-navbar-nav>li {
    background: black;
    padding: 10px;
    font-size: 20px;
    border-bottom: 1px solid white;
  }

  .uk-navbar-nav>li.uk-active>a {
    color: white;
    font-weight: bold;
  }

  .uk-navbar-nav>li>a {
    color: #edf2f7;
  }
}

/* Utilities */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.uk-navbar {
  min-height: 50px;
  padding: 10px;
}

.uk-navbar button.navbar-toggle {
  position: absolute;
  right: 0;
  top: 0;
  padding: 10px 12px;
  margin-top: 14px;
  margin-right: 15px;
  margin-bottom: 12px;
  background-color: transparent;
  background-image: none;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.uk-navbar button.navbar-toggle .icon-bar {
  display: block;
  width: 22px;
  height: 2px;
  border-radius: 1px;
  background-color: white;
}

.uk-navbar button.navbar-toggle .icon-bar+.icon-bar {
  margin-top: 4px;
}

.uk-navbar .uk-navbar-nav {
  flex-wrap: wrap-reverse;
  align-self: center;
  align-items: center;
}

.uk-navbar .uk-navbar-nav>li>a {
  min-height: auto;
  line-height: 3em;
}

#mainNavbar .uk-navbar-right {
  display: flex;
  align-items: center;
}

#mainNavbar .uk-navbar-nav_a {
  display: inline-flex;
  align-items: center;
}

/* General Layout Colors */
.our-bg-color {
  background-color: #fff;
}

.uk-radio {
  background-color: #e5e9ff;
}

.uk-table th {
  color: #565656;
  font-weight: bold;
}

.uk-modal {
  transition: opacity 0.12s cubic-bezier(0.6, -0.28, 0.74, 0.05);
}

.uk-dropdown {
  box-shadow: 0 2px 12px rgb(0 0 0 / 82%) !important;
}

.uk-dropdown-nav>li>a {
  color: #000;
}

/* Color Variables and Extends */
.clr-yellow {
  background-color: #ffc043;
  color: #000;
}

.clr-yellow-2 {
  background-color: #997328;
}

.clr-yellow-3 {
  background-color: #bc8b2c;
}

.clr-blue-5 {
  background-color: #535fcf;
}

.clr-storm-1 {
  background-color: #142328;
  color: #ffffff;
}

.clr-storm-6 {
  background-color: #ccdfe5;
}

.clr-storm-7 {
  background-color: #ccdfe5;
  color: #000000;
}

.clr-red {
  background-color: #e11900;
  color: #ffffff;
}

.clr-red-2 {
  background-color: #870f00;
  color: #ffffff;
}

.clr-red-3 {
  background-color: #ab1300;
  color: #ffffff;
}

.clr-red-4 {
  background-color: #e11900;
  color: #ffffff;
}

.clr-green-2 {
  background-color: #156a41;
  color: #ffffff;
}

.clr-green-3 {
  background-color: #09864a;
  color: #ffffff;
}

.clr-green-4 {
  background-color: #05a357;
  color: #ffffff;
}

.clr-green-5 {
  background-color: #06c167;
  color: #000000;
}

.clr-orange-4 {
  background-color: #ff6937;
}

/* Labels */
.uk-label-danger {
  @extend .clr-red;
}

.uk-label-orange {
  @extend .clr-orange-4;
}

.uk-label-light-blue {
  @extend .clr-blue-5;
}

.uk-label-olive {
  @extend .clr-green-2;
}

.uk-label-yellow {
  @extend .clr-yellow-3;
}

.uk-label-dark-red {
  @extend .clr-red-3;
}

.uk-label-green {
  @extend .clr-green-4;
}

.uk-label-success {
  background-color: #09864a;
}

.uk-label-gray {
  background-color: #8fa3ad;
}

/* Button */
button.green-button {
  @extend .clr-green-3;
}

/* Background */
.uk-section-storm {
  @extend .clr-storm-1;

  h2 {
    color: #ffffff;
  }
}

/* Switcher */
.switcher.uk-button.uk-active {
  border-color: #142328;
  border: 2px solid;
  font-weight: 500;
}

/* Note */
.multiselect__option--selected.multiselect__option--highlight {
  background: #41b883 !important;
  color: #fff;
}

.uk-text-medium {
  font-size: 1.2rem;
  line-height: 1.5;
}

/* Icons */
.uk-icon-light {
  color: rgba(0, 0, 0, 0.38);
}

.uk-icon-green {
  color: #09864a;
}

.uk-icon-dark {
  color: black;
}

/* Subnav */
.uk-subnav-pill {
  li:not(.uk-active)>a {
    background-color: #8080802e;
    color: black;
  }

  li>a :hover {
    background-color: unset;
  }
}

.logo {
  color: white;
  font-weight: bold;
  min-height: 20px;
  font-size: xx-large;
}

/* Alert */
.uk-alert-success {
  color: #10462d;
}

/* Dropdown */
.uk-dropdown-nav>li>a:hover,
.uk-dropdown-nav>li>a:focus,
.uk-dropdown-nav>li.uk-active>a {
  background: #e4e4e4;
}

/* Tab */
.uk-tab-big.uk-tab>*>a {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>